import React, { useState } from 'react';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { MessageSquare, Copy, Check, RefreshCw, Grid, Calendar, Award, User, ExternalLink } from 'lucide-react';
import Button from '../ui/Button';
import { Quiz } from '../../lib/quizzes';
import { CalculatedResult } from '../../lib/personalityQuiz';
import useLocalStorage from '../../lib/useLocalStorage';
import { fadeInUp, stagger } from '../../lib/animations';

interface QuizResultViewProps {
  quiz: Quiz;
  result: CalculatedResult;
  userName: string;
  isSharedView?: boolean;
  sharedBy?: string;
  onRetake: () => void;
}

const mdComponents: Components = {
  h1: ({ children }) => (
    <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mt-8 mb-4 leading-tight">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="font-display text-xl sm:text-2xl font-bold text-white mt-8 mb-3 pb-2 border-b border-slate-800">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-100 mt-6 mb-2">
      {children}
    </h3>
  ),
  p: ({ children }) => (
    <p className="text-slate-300 leading-relaxed text-base mb-4">
      {children}
    </p>
  ),
  strong: ({ children }) => (
    <strong className="text-amber-400 font-semibold">{children}</strong>
  ),
  em: ({ children }) => <em className="text-slate-300 italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="list-disc list-outside pl-6 text-slate-300 mb-4 space-y-2">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside pl-6 text-slate-300 mb-4 space-y-2">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="text-slate-300 leading-relaxed">{children}</li>,
};

interface HistoryEntry {
  quizSlug: string;
  quizTitle: string;
  userName: string;
  resultShortName: string;
  resultPersona: string;
  finishedAt: string;
}

const QuizResultView: React.FC<QuizResultViewProps> = ({
  quiz,
  result,
  userName,
  isSharedView = false,
  sharedBy = '',
  onRetake,
}) => {
  const [copied, setCopied] = useState(false);
  const [history] = useLocalStorage<HistoryEntry[]>('quiz:history', []);

  // Filter history to current quiz
  const quizHistory = history.filter((h) => h.quizSlug === quiz.slug);

  // Generate share URL
  const shareUrl = `${window.location.origin}/quiz/${quiz.slug}?name=${encodeURIComponent(
    isSharedView ? sharedBy : userName
  )}&result=${encodeURIComponent(result.shortName)}`;

  const utmShareUrl = `${shareUrl}&utm_source=whatsapp&utm_medium=social&utm_campaign=quiz-share&utm_content=${quiz.slug}`;

  // WhatsApp pre-filled message
  const shareText = isSharedView
    ? `Fiz o teste "${quiz.title}" de qual carro eu seria e o resultado do meu amigo deu ${result.shortName}! 😂 Faz o teu aí pra ver se você é raiz ou boy: ${window.location.origin}/quiz/${quiz.slug}?utm_source=whatsapp`
    : `Fiz o teste de qual carro brasileiro eu sou e deu ${result.shortName}! 😂 Faz o teu aí pra ver se você é raiz ou boy: ${utmShareUrl}`;

  const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
      {/* Shared Header Banner */}
      {isSharedView && sharedBy && (
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate="visible"
          className="mb-8 p-5 rounded-2xl bg-amber-400/10 border border-amber-400/30 text-center"
        >
          <p className="text-sm sm:text-base text-amber-400 font-medium">
            Você está vendo o resultado de <strong className="text-white font-bold">{sharedBy}</strong>!
          </p>
          <div className="mt-3">
            <a
              href={`/quiz/${quiz.slug}`}
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-white hover:text-amber-400 font-bold underline transition-colors"
            >
              Descubra qual carro você seria! Fazer o teste agora
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
        </motion.div>
      )}

      {/* Main Result Card */}
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="overflow-hidden rounded-3xl bg-slate-900 border border-slate-800 p-6 sm:p-10 shadow-2xl mb-10"
      >
        {/* Header */}
        <motion.div variants={fadeInUp} className="text-center pb-8 border-b border-slate-800 mb-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400/10 mb-4">
            <Award className="h-7 w-7 text-amber-400" strokeWidth={1.8} />
          </div>
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-amber-400 bg-amber-400/10 px-3 py-1 rounded-full border border-amber-400/20 mb-3">
            Personalidade: {result.persona}
          </span>
          <h2 className="font-display text-2xl sm:text-4xl font-extrabold text-white leading-tight">
            {result.title}
          </h2>
        </motion.div>

        {/* Content Description */}
        <motion.div variants={fadeInUp} className="prose prose-invert max-w-none mb-8">
          <ReactMarkdown components={mdComponents}>{result.description}</ReactMarkdown>
        </motion.div>

        {/* Share Section */}
        <motion.div variants={fadeInUp} className="border-t border-slate-800 pt-8 mt-8">
          <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
            <User className="h-5 w-5 text-amber-400" />
            Compartilhe seu resultado!
          </h3>
          <p className="text-sm text-slate-400 mb-5 leading-relaxed">
            Mostre para todo mundo o seu resultado! Copie o link abaixo ou compartilhe diretamente no WhatsApp.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            {/* Copy Link input */}
            <div className="flex flex-1 items-center gap-2 rounded-xl bg-slate-950 border border-slate-800 px-4 py-3 text-sm text-slate-300 overflow-hidden">
              <span className="truncate flex-1 select-all">{shareUrl}</span>
              <button
                onClick={handleCopyLink}
                className="text-slate-400 hover:text-amber-400 transition-colors shrink-0"
                aria-label="Copiar link"
              >
                {copied ? <Check className="h-5 w-5 text-green-400" /> : <Copy className="h-5 w-5" />}
              </button>
            </div>

            {/* WhatsApp Share Button */}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-green-500 hover:bg-green-600 active:bg-green-700 text-slate-950 font-bold px-6 py-3 transition-colors duration-200 shadow-lg shadow-green-500/10 shrink-0 text-sm"
            >
              <MessageSquare className="h-5 w-5" strokeWidth={2} />
              Enviar no WhatsApp
            </a>
          </div>
        </motion.div>

        {/* Actions Row */}
        <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-800 pt-8 mt-8 text-xs text-slate-500">
          <button
            onClick={onRetake}
            className="flex items-center gap-2 text-sm font-bold text-amber-400 hover:text-amber-300 transition-colors py-2 px-4 rounded-xl border border-slate-800 hover:border-amber-400/20 bg-slate-950/40"
          >
            <RefreshCw className="h-4 w-4" />
            Refazer Teste
          </button>

          <a
            href="/quiz"
            className="flex items-center gap-2 text-sm font-bold text-slate-400 hover:text-white transition-colors py-2 px-4 rounded-xl border border-transparent hover:border-slate-800"
          >
            <Grid className="h-4 w-4" />
            Ver todos os quizzes
          </a>
        </motion.div>
      </motion.div>

      {/* History Card Section */}
      {!isSharedView && quizHistory.length > 1 && (
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="rounded-3xl bg-slate-900/60 border border-slate-800/80 p-6 sm:p-8"
        >
          <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-amber-400" />
            Seu Histórico de Resultados
          </h3>
          <div className="space-y-3">
            {quizHistory
              .sort((a, b) => new Date(b.finishedAt).getTime() - new Date(a.finishedAt).getTime())
              .slice(0, 5)
              .map((entry, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-4 rounded-xl bg-slate-950/40 border border-slate-800/50 text-sm"
                >
                  <div>
                    <span className="font-bold text-white">{entry.resultShortName}</span>
                    <span className="text-slate-500 mx-2">•</span>
                    <span className="text-slate-400">{entry.resultPersona}</span>
                  </div>
                  <span className="text-xs text-slate-500">
                    {new Date(entry.finishedAt).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default QuizResultView;
